import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Button,
    Card,
    Col,
    Form,
    Input,
    InputNumber,
    Modal,
    Pagination,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from "antd";

import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    SearchOutlined,
} from "@ant-design/icons";

import Swal from "sweetalert2";

import { pdf } from "@react-pdf/renderer";
import TeacherBioDataDivision from "./TeacherBioDataDivision";
import "./TeacherDataForm.css";
import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";
import TeacherDataPDF from "./TeacherDataPDF";


const {
    Title,
    Text,
} = Typography;


const TeacherDataForm = () => {

    const [form] = Form.useForm();

    const {
        selectedYear,
    } = useAcademicYear();

    const [open, setOpen] = useState(false);

    const [loading, setLoading] = useState(false);

    const [tableLoading, setTableLoading] = useState(false);

    const [teachers, setTeachers] = useState([]);

    const [users, setUsers] = useState([]);

    const [divisions, setDivisions] = useState([]);

    const [subjects, setSubjects] = useState([]);

    const [gradeClasses, setGradeClasses] = useState([]);

    const [editingId, setEditingId] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");

    const [currentPage, setCurrentPage] = useState(1);

    const [pageSize, setPageSize] = useState(8);

    const [messageApi, contextHolder] =
        message.useMessage();


    // ==========================================================
    // INITIAL DATA
    // ==========================================================

    useEffect(() => {

        fetchUsers();
        fetchDivisions();
        fetchSubjects();
        fetchGradeClasses();

    }, []);


    // ==========================================================
    // FETCH TEACHERS WHEN YEAR CHANGES
    // ==========================================================

    useEffect(() => {

        if (selectedYear?.id) {

            fetchTeachers();

        } else {

            setTeachers([]);

        }

        setCurrentPage(1);

    }, [selectedYear]);


    // ==========================================================
    // FETCH TEACHERS
    // ==========================================================

    const fetchTeachers = async () => {

        if (!selectedYear?.id) {
            return;
        }

        try {

            setTableLoading(true);

            const response = await api.get(
                "/api/users/teacher-data/",
                {
                    params: {
                        academic_year:
                            selectedYear.id,
                    },
                }
            );

            const data =
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || [];

            setTeachers(data);

        } catch (error) {

            console.error(
                "FETCH TEACHERS ERROR:",
                error.response?.data || error
            );

            messageApi.error(
                "Unable to load teacher data."
            );

        } finally {

            setTableLoading(false);

        }

    };


    // ==========================================================
    // FETCH USERS
    // ==========================================================

    const fetchUsers = async () => {

        try {

            const response = await api.get(
                "/api/teacherslist/"
            );

            setUsers(
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []
            );

        } catch (error) {

            console.error(
                "FETCH USERS ERROR:",
                error.response?.data || error
            );

            messageApi.error(
                "Unable to load users."
            );

        }

    };


    // ==========================================================
    // FETCH DIVISIONS
    // ==========================================================

    const fetchDivisions = async () => {

        try {

            const response = await api.get(
                "/api/users/divisions/"
            );

            setDivisions(
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []
            );

        } catch (error) {

            console.error(
                "FETCH DIVISIONS ERROR:",
                error.response?.data || error
            );

            messageApi.error(
                "Unable to load divisions."
            );

        }

    };


    // ==========================================================
    // FETCH SUBJECTS
    // ==========================================================

    const fetchSubjects = async () => {

        try {

            const response = await api.get(
                "/api/users/subjects/"
            );

            setSubjects(
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []
            );

        } catch (error) {

            console.error(
                "FETCH SUBJECTS ERROR:",
                error.response?.data || error
            );

            messageApi.error(
                "Unable to load subjects."
            );

        }

    };


    // ==========================================================
    // FETCH GRADE CLASSES
    // ==========================================================

    const fetchGradeClasses = async () => {

        try {

            const response = await api.get(
                "/api/users/grade-classes/"
            );

            setGradeClasses(
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []
            );

        } catch (error) {

            console.error(
                "FETCH GRADE CLASSES ERROR:",
                error.response?.data || error
            );

            messageApi.error(
                "Unable to load grade classes."
            );

        }

    };


    // ==========================================================
    // SEARCH
    // ==========================================================

    const filteredTeachers = useMemo(() => {

        const search =
            searchTerm
                .toLowerCase()
                .trim();

        if (!search) {
            return teachers;
        }

        return teachers.filter(
            (teacher) =>
                teacher.full_name
                    ?.toLowerCase()
                    .includes(search)
                ||
                teacher.username?.username
                    ?.toLowerCase()
                    .includes(search)
                ||
                teacher.employment_status_display
                    ?.toLowerCase()
                    .includes(search)
                ||
                teacher.employment_status
                    ?.toLowerCase()
                    .includes(search)
        );

    }, [teachers, searchTerm]);


    // ==========================================================
    // DIVISION NAMES
    // ==========================================================

    const getDivisionNames = (
        ids = []
    ) => {

        if (!Array.isArray(ids)) {
            return [];
        }

        return ids
            .map(
                (id) =>
                    divisions.find(
                        (division) =>
                            division.id === id
                    )?.name
            )
            .filter(Boolean);

    };


    // ==========================================================
    // SUBJECT NAMES
    // ==========================================================

    const getSubjectNames = (
        ids = []
    ) => {

        if (!Array.isArray(ids)) {
            return "";
        }

        return ids
            .map(
                (id) =>
                    subjects.find(
                        (subject) =>
                            subject.id === id
                    )?.name
            )
            .filter(Boolean)
            .join(", ");

    };


    // ==========================================================
    // GRADE NAMES
    // ==========================================================

    const getGradeNames = (
        ids = []
    ) => {

        if (!Array.isArray(ids)) {
            return "";
        }

        return ids
            .map(
                (id) =>
                    gradeClasses.find(
                        (grade) =>
                            grade.id === id
                    )?.name
            )
            .filter(Boolean)
            .join(", ");

    };


    // ==========================================================
    // STATUS HELPERS
    // ==========================================================

    const getEmploymentStatus =
        (teacher) => {

            return (
                teacher?.employment_status ||
                "active"
            );

        };


    const getEmploymentStatusDisplay =
        (teacher) => {

            return (
                teacher?.employment_status_display ||
                (
                    getEmploymentStatus(
                        teacher
                    ) === "fired"
                        ? "Sacked / Fired"
                        : getEmploymentStatus(
                            teacher
                        ).charAt(0).toUpperCase()
                        +
                        getEmploymentStatus(
                            teacher
                        ).slice(1)
                )
            );

        };


    const getStatusTagColor =
        (status) => {

            switch (status) {

                case "active":
                    return "green";

                case "removed":
                    return "orange";

                case "fired":
                    return "red";

                default:
                    return "default";

            }

        };


    // ==========================================================
    // RESET FORM
    // ==========================================================

    const resetForm = () => {

        form.resetFields();

        setEditingId(null);

    };


    // ==========================================================
    // OPEN ADD MODAL
    // ==========================================================

    const openAddModal = () => {

        resetForm();

        form.setFieldsValue({
            employment_status:
                "active",
            status_reason:
                "",
        });

        setOpen(true);

    };


    // ==========================================================
    // EDIT TEACHER
    // ==========================================================

    const handleEdit = (teacher) => {

        const teacherDivisions =
            Array.isArray(
                teacher.divisions
            )
                ? teacher.divisions
                : teacher.division
                    ? [teacher.division]
                    : [];


        const employmentStatus =
            teacher.employment_status ||
            "active";


        form.setFieldsValue({

            username:
                teacher.username,

            full_name:
                teacher.full_name,

            qualification:
                teacher.qualification,

            divisions:
                teacherDivisions,

            dependent_student:
                teacher.dependent_student,

            contact:
                teacher.contact,

            salary:
                teacher.salary,

            date_add:
                teacher.date_add,

            subjects:
                Array.isArray(
                    teacher.subjects
                )
                    ? teacher.subjects
                    : [],

            grade_class:
                Array.isArray(
                    teacher.grade_class
                )
                    ? teacher.grade_class
                    : [],

            employment_status:
                employmentStatus,

            status_reason:
                teacher.status_reason || "",

        });


        setEditingId(
            teacher.id
        );

        setOpen(true);

    };


    // ==========================================================
    // DOWNLOAD TEACHER PDF
    // ==========================================================

    const downloadTeacherPDF = async (
        teacher
    ) => {

        try {

            const divisionNames =
                Array.isArray(
                    teacher.division_names
                )
                    ? teacher.division_names
                    : getDivisionNames(
                        Array.isArray(
                            teacher.divisions
                        )
                            ? teacher.divisions
                            : teacher.division
                                ? [teacher.division]
                                : []
                    );


            const blob = await pdf(
                <TeacherDataPDF
                    teacher={teacher}

                    divisionNames={
                        divisionNames
                    }

                    divisionName={
                        divisionNames.join(", ")
                    }

                    subjectNames={
                        getSubjectNames(
                            teacher.subjects
                        )
                    }

                    gradeNames={
                        getGradeNames(
                            teacher.grade_class
                        )
                    }
                />
            ).toBlob();


            const url =
                URL.createObjectURL(
                    blob
                );


            const link =
                document.createElement(
                    "a"
                );


            link.href = url;


            link.download =
                `${teacher.full_name}_Teacher_Data.pdf`;


            document.body.appendChild(
                link
            );


            link.click();


            document.body.removeChild(
                link
            );


            URL.revokeObjectURL(
                url
            );


        } catch (error) {

            console.error(
                "PDF ERROR:",
                error
            );

            messageApi.error(
                "Unable to generate teacher PDF."
            );

        }

    };


    // ==========================================================
    // SUBMIT
    // ==========================================================

    const handleSubmit = async (
        values
    ) => {

        if (!selectedYear?.id) {

            messageApi.error(
                "Please select an academic year first."
            );

            return;

        }


        const employmentStatus =
            values.employment_status ||
            "active";


        const statusReason =
            (
                values.status_reason ||
                ""
            ).trim();


        // ------------------------------------------------------
        // REQUIRE REASON FOR REMOVED / FIRED
        // ------------------------------------------------------

        if (
            (
                employmentStatus === "removed" ||
                employmentStatus === "fired"
            )
            &&
            !statusReason
        ) {

            messageApi.error(
                "Please provide a reason for the employment status."
            );

            return;

        }


        // ------------------------------------------------------
        // CONFIRM NON-ACTIVE STATUS
        // ------------------------------------------------------

        if (
            editingId &&
            (
                employmentStatus === "removed" ||
                employmentStatus === "fired"
            )
        ) {

            const statusLabel =
                employmentStatus === "fired"
                    ? "SACKED / FIRED"
                    : "REMOVED";


            const result =
                await Swal.fire({

                    title:
                        `Set Teacher as ${statusLabel}?`,

                    html:
                        `
                        <div style="text-align:left;">
                            <p>
                                This status applies only to:
                            </p>

                            <p>
                                <strong>
                                    ${selectedYear.name}
                                </strong>
                            </p>

                            <p>
                                The teacher's historical record
                                will be preserved.
                            </p>

                            <p>
                                Login will be blocked and this teacher won't have acess to the System
                            </p>
                        </div>
                        `,

                    icon:
                        "warning",

                    showCancelButton:
                        true,

                    confirmButtonText:
                        "Yes, Continue",

                    cancelButtonText:
                        "Cancel",

                    confirmButtonColor:
                        "#d9363e",

                    cancelButtonColor:
                        "#111111",

                });


            if (!result.isConfirmed) {
                return;
            }

        }


        try {

            setLoading(true);


            const payload = {

                ...values,

                academic_year:
                    selectedYear.id,

                employment_status:
                    employmentStatus,

                status_reason:
                    statusReason,

                divisions:
                    Array.isArray(
                        values.divisions
                    )
                        ? values.divisions
                        : [],

                subjects:
                    Array.isArray(
                        values.subjects
                    )
                        ? values.subjects
                        : [],

                grade_class:
                    Array.isArray(
                        values.grade_class
                    )
                        ? values.grade_class
                        : [],

            };


            let response;


            if (editingId) {

                response = await api.put(
                    `/api/users/teacher-data/${editingId}/`,
                    payload
                );

            } else {

                response = await api.post(
                    "/api/users/teacher-data/",
                    payload
                );

            }


            const teacherId =
                response.data.id ||
                editingId;


            const teacherResponse =
                await api.get(
                    `/api/users/teacher-data/${teacherId}/`
                );


            const savedTeacher =
                teacherResponse.data;


            await fetchTeachers();


            setOpen(false);

            resetForm();


            const savedStatus =
                savedTeacher.employment_status ||
                employmentStatus;


            let successTitle =
                editingId
                    ? "Teacher Updated"
                    : "Teacher Added";


            let successText =
                "Teacher data was saved successfully.";


            if (
                savedStatus === "removed"
            ) {

                successTitle =
                    "Teacher Marked Removed";

                successText =
                    `The teacher is now marked as REMOVED for ${selectedYear.name}. Login is blocked for this academic year.`;

            }


            if (
                savedStatus === "fired"
            ) {

                successTitle =
                    "Teacher Marked Sacked / Fired";

                successText =
                    `The teacher is now marked as SACKED / FIRED for ${selectedYear.name}. Login is blocked for this academic year.`;

            }


            await Swal.fire({

                icon:
                    "success",

                title:
                    successTitle,

                text:
                    successText,

                confirmButtonColor:
                    "#ff7900",

            });


            await downloadTeacherPDF(
                savedTeacher
            );


        } catch (error) {

            console.error(
                "SAVE TEACHER ERROR:",
                error.response?.data ||
                    error
            );


            const backendError =
                error.response?.data;


            let errorText =
                "Something went wrong.";


            if (
                backendError?.detail
            ) {

                errorText =
                    backendError.detail;

            } else if (
                backendError &&
                typeof backendError === "object"
            ) {

                errorText =
                    Object.entries(
                        backendError
                    )
                        .map(
                            ([field, errors]) =>
                                `${field}: ${
                                    Array.isArray(errors)
                                        ? errors.join(", ")
                                        : errors
                                }`
                        )
                        .join("\n");

            }


            Swal.fire({

                icon:
                    "error",

                title:
                    "Operation Failed",

                text:
                    errorText,

                confirmButtonColor:
                    "#111111",

            });

        } finally {

            setLoading(false);

        }

    };


    // ==========================================================
    // DELETE
    // ==========================================================

    const handleDelete = async (
        id
    ) => {

        const result =
            await Swal.fire({

                title:
                    "Delete Teacher Record?",

                html:
                    `
                    <p>
                        This will permanently delete
                        this academic-year teacher record.
                    </p>

                    <p>
                        <strong>
                            For employment termination,
                            use Removed or Sacked / Fired
                            instead.
                        </strong>
                    </p>
                    `,

                icon:
                    "warning",

                showCancelButton:
                    true,

                confirmButtonColor:
                    "#d9363e",

                cancelButtonColor:
                    "#111111",

                confirmButtonText:
                    "Yes, Delete",

            });


        if (!result.isConfirmed) {
            return;
        }


        try {

            await api.delete(
                `/api/users/teacher-data/${id}/`
            );


            await fetchTeachers();


            Swal.fire({

                icon:
                    "success",

                title:
                    "Deleted",

                text:
                    "Teacher record deleted successfully.",

                confirmButtonColor:
                    "#ff7900",

            });

        } catch (error) {

            console.error(
                "DELETE TEACHER ERROR:",
                error.response?.data ||
                    error
            );


            messageApi.error(
                error.response?.data?.detail ||
                "Unable to delete teacher record."
            );

        }

    };


    // ==========================================================
    // TABLE COLUMNS
    // ==========================================================

    const columns = [

        // ------------------------------------------------------
        // TEACHER
        // ------------------------------------------------------

        {

            title:
                "Teacher",

            key:
                "teacher",

            fixed:
                "left",

            width:
                250,

            render:
                (_, record) => (

                    <div
                        className="teacher-name-cell"
                    >

                        <div
                            className="teacher-avatar"
                        >

                            {record.full_name
                                ?.charAt(0)
                                ?.toUpperCase()}

                        </div>


                        <div>

                            <Text strong>

                                {record.full_name}

                            </Text>


                            <br />


                            <Text type="secondary">

                                {record.contact ||
                                    "No contact"}

                            </Text>

                        </div>

                    </div>

                ),

        },


        // ------------------------------------------------------
        // EMPLOYMENT STATUS
        // ------------------------------------------------------

        {

            title:
                "Employment Status",

            key:
                "employment_status",

            width:
                190,

            render:
                (_, record) => {

                    const status =
                        getEmploymentStatus(
                            record
                        );

                    return (

                        <Space
                            direction="vertical"
                            size={4}
                        >

                            <Tag
                                color={
                                    getStatusTagColor(
                                        status
                                    )
                                }
                                style={{
                                    fontWeight: 700,
                                }}
                            >

                                {getEmploymentStatusDisplay(
                                    record
                                )}

                            </Tag>


                            {record.academic_year_name && (

                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 11,
                                    }}
                                >

                                    {record.academic_year_name}

                                </Text>

                            )}

                        </Space>

                    );

                },

        },


        // ------------------------------------------------------
        // STATUS REASON
        // ------------------------------------------------------

        {

            title:
                "Status Reason",

            key:
                "status_reason",

            width:
                280,

            render:
                (_, record) => {

                    if (
                        !record.status_reason
                    ) {

                        return (
                            <Text type="secondary">
                                —
                            </Text>
                        );

                    }

                    return (

                        <Text
                            ellipsis={{
                                tooltip:
                                    record.status_reason,
                            }}
                        >

                            {record.status_reason}

                        </Text>

                    );

                },

        },


        // ------------------------------------------------------
        // QUALIFICATION
        // ------------------------------------------------------

        {

            title:
                "Qualification",

            dataIndex:
                "qualification",

            width:
                180,

        },


        // ------------------------------------------------------
        // DIVISIONS
        // ------------------------------------------------------

        {

            title:
                "Divisions",

            key:
                "divisions",

            width:
                260,

            render:
                (_, record) => {

                    const divisionIds =
                        Array.isArray(
                            record.divisions
                        )
                            ? record.divisions
                            : record.division
                                ? [record.division]
                                : [];


                    const names =
                        getDivisionNames(
                            divisionIds
                        );


                    if (!names.length) {

                        return (
                            <Text type="secondary">
                                None
                            </Text>
                        );

                    }


                    return (

                        <Space
                            size={[
                                4,
                                4
                            ]}
                            wrap
                        >

                            {names.map(
                                (
                                    name,
                                    index
                                ) => (

                                    <Tag
                                        key={
                                            `${name}-${index}`
                                        }
                                        color="orange"
                                    >
                                        {name}
                                    </Tag>

                                )
                            )}

                        </Space>

                    );

                },

        },


        // ------------------------------------------------------
        // SALARY
        // ------------------------------------------------------

        {

            title:
                "Salary",

            dataIndex:
                "salary",

            width:
                130,

            render:
                (value) => (

                    <Text strong>

                        {value || "0"}

                    </Text>

                ),

        },


        // ------------------------------------------------------
        // SUBJECTS
        // ------------------------------------------------------

        {

            title:
                "Subjects",

            key:
                "subjects",

            width:
                260,

            render:
                (_, record) => (

                    <div
                        className="table-text"
                    >

                        {getSubjectNames(
                            record.subjects
                        ) || "None"}

                    </div>

                ),

        },


        // ------------------------------------------------------
        // GRADE CLASSES
        // ------------------------------------------------------

        {

            title:
                "Grade Classes",

            key:
                "grade_class",

            width:
                260,

            render:
                (_, record) => (

                    <div
                        className="table-text"
                    >

                        {getGradeNames(
                            record.grade_class
                        ) || "None"}

                    </div>

                ),

        },


        // ------------------------------------------------------
        // ACTIONS
        // ------------------------------------------------------

        {

            title:
                "Actions",

            key:
                "actions",

            fixed:
                "right",

            width:
                170,

            render:
                (_, record) => (

                    <Space>

                        <Button
                            className="edit-btn"
                            icon={
                                <EditOutlined />
                            }
                            onClick={() =>
                                handleEdit(
                                    record
                                )
                            }
                        >
                            Edit
                        </Button>


                        <Button
                            danger
                            icon={
                                <DeleteOutlined />
                            }
                            onClick={() =>
                                handleDelete(
                                    record.id
                                )
                            }
                        />

                    </Space>

                ),

        },

    ];


    // ==========================================================
    // RENDER
    // ==========================================================

    return (

        <>

            {contextHolder}


            <div
                className="teacher-page"
            >

                {/* ==================================================
                    HEADER
                ================================================== */}

                <div
                    className="teacher-page-header"
                >

                    <div>

                        <Title level={2}>

                            Teacher Management

                        </Title>


                        <Text>

                            Manage teachers,
                            qualifications,
                            assignments,
                            salary,
                            and employment status.

                        </Text>

                    </div>


                    <Button
                        type="primary"
                        size="large"
                        icon={
                            <PlusOutlined />
                        }
                        onClick={
                            openAddModal
                        }
                    >

                        Add Teacher

                    </Button>

                </div>

<div>
    <TeacherBioDataDivision />
</div>

                {/* ==================================================
                    CONTROLS
                ================================================== */}

                <Card
                    className="teacher-control-card"
                >

                    <Row
                        gutter={[
                            16,
                            16
                        ]}
                        align="middle"
                    >

                        <Col
                            xs={24}
                            sm={16}
                            md={10}
                            lg={8}
                        >

                            <Input
                                size="large"
                                prefix={
                                    <SearchOutlined />
                                }
                                placeholder={
                                    "Search teacher by name, username or status..."
                                }
                                value={
                                    searchTerm
                                }
                                onChange={(e) => {

                                    setSearchTerm(
                                        e.target.value
                                    );

                                    setCurrentPage(
                                        1
                                    );

                                }}
                                allowClear
                            />

                        </Col>


                        <Col
                            xs={24}
                            sm={8}
                            md={14}
                            lg={16}
                            className="teacher-stats"
                        >

                            <Tag
                                color="orange"
                            >

                                Total Teachers:{" "}

                                {teachers.length}

                            </Tag>


                            <Tag
                                color="black"
                            >

                                Academic Year:{" "}

                                {selectedYear?.name ||
                                    "Not Selected"}

                            </Tag>


                            {selectedYear && (

                                <Tag
                                    color="blue"
                                >

                                    Status applies to this year

                                </Tag>

                            )}

                        </Col>

                    </Row>

                </Card>


                {/* ==================================================
                    TABLE
                ================================================== */}

                <Card
                    className="teacher-table-card"
                >

                    <Spin
                        spinning={
                            tableLoading
                        }
                    >

                        <Table
                            rowKey="id"
                            columns={
                                columns
                            }
                            dataSource={
                                filteredTeachers.slice(
                                    (
                                        currentPage -
                                        1
                                    ) *
                                        pageSize,

                                    currentPage *
                                        pageSize
                                )
                            }
                            pagination={
                                false
                            }
                            scroll={{
                                x: 1750,
                            }}
                            bordered
                            size="middle"
                        />

                    </Spin>


                    <div
                        className="teacher-pagination"
                    >

                        <Pagination
                            current={
                                currentPage
                            }
                            pageSize={
                                pageSize
                            }
                            total={
                                filteredTeachers.length
                            }
                            showSizeChanger
                            pageSizeOptions={[
                                "8",
                                "16",
                                "32",
                                "64",
                            ]}
                            onChange={(
                                page,
                                size
                            ) => {

                                setCurrentPage(
                                    page
                                );

                                setPageSize(
                                    size
                                );

                            }}
                            showTotal={
                                (total) =>
                                    `Total ${total} teachers`
                            }
                        />

                    </div>

                </Card>


                {/* ==================================================
                    MODAL
                ================================================== */}

                <Modal
                    open={open}
                    title={

                        <div
                            className="modal-title"
                        >

                            {editingId
                                ? "Edit Teacher"
                                : "Add New Teacher"}

                        </div>

                    }
                    onCancel={() => {

                        setOpen(false);

                        resetForm();

                    }}
                    footer={null}
                    width={850}
                    centered
                    destroyOnClose
                >

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={
                            handleSubmit
                        }
                    >

                        <Row
                            gutter={[
                                16,
                                0
                            ]}
                        >

                            {/* ==================================================
                                ACADEMIC YEAR
                            ================================================== */}

                            <Col
                                xs={24}
                            >

                                <Form.Item
                                    label="Academic Year"
                                >

                                    <Input
                                        value={
                                            selectedYear?.name ||
                                            ""
                                        }
                                        disabled
                                        style={{
                                            fontWeight: 700,
                                        }}
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                USERNAME
                            ================================================== */}

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label="Teacher Username"
                                    name="username"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            message:
                                                "Please select a username.",
                                        },
                                    ]}
                                >

                                    <Select
                                        showSearch
                                        placeholder="Select username"
                                        optionFilterProp="label"
                                        options={
                                            users.map(
                                                (
                                                    user
                                                ) => ({
                                                    value:
                                                        user.id,

                                                    label:
                                                        user.username,
                                                })
                                            )
                                        }
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                FULL NAME
                            ================================================== */}

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label="Full Name"
                                    name="full_name"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            message:
                                                "Enter full name.",
                                        },
                                    ]}
                                >

                                    <Input
                                        placeholder="Teacher full name"
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                EMPLOYMENT STATUS
                            ================================================== */}

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label={
                                        `Employment Status — ${selectedYear?.name || "Academic Year"}`
                                    }
                                    name="employment_status"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            message:
                                                "Please select employment status.",
                                        },
                                    ]}
                                >

                                    <Select
                                        placeholder="Select employment status"
                                        options={[
                                            {
                                                value:
                                                    "active",

                                                label:
                                                    "Active — Login Allowed",

                                            },
                                            {
                                                value:
                                                    "removed",

                                                label:
                                                    "Removed — Login Blocked",

                                            },
                                            {
                                                value:
                                                    "fired",

                                                label:
                                                    "Sacked / Fired — Login Blocked",

                                            },
                                        ]}
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                STATUS REASON
                            ================================================== */}

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    noStyle
                                    shouldUpdate={(
                                        prevValues,
                                        currentValues
                                    ) =>
                                        prevValues.employment_status !==
                                        currentValues.employment_status
                                    }
                                >

                                    {({
                                        getFieldValue,
                                    }) => {

                                        const status =
                                            getFieldValue(
                                                "employment_status"
                                            );


                                        const isBlocked =
                                            status ===
                                                "removed" ||
                                            status ===
                                                "fired";


                                        return (

                                            <Form.Item
                                                label={
                                                    isBlocked
                                                        ? "Status Reason — Required"
                                                        : "Status Reason"
                                                }
                                                name="status_reason"
                                                rules={
                                                    isBlocked
                                                        ? [
                                                            {
                                                                required:
                                                                    true,

                                                                whitespace:
                                                                    true,

                                                                message:
                                                                    "Please provide the reason.",
                                                            },
                                                        ]
                                                        : []
                                                }
                                            >

                                                <Input.TextArea
                                                    rows={
                                                        3
                                                    }
                                                    placeholder={
                                                        isBlocked
                                                            ? "Explain why the teacher was removed or sacked/fired..."
                                                            : "Optional status note..."
                                                    }
                                                    maxLength={
                                                        2000
                                                    }
                                                    showCount
                                                />

                                            </Form.Item>

                                        );

                                    }}

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                QUALIFICATION
                            ================================================== */}

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label="Qualification"
                                    name="qualification"
                                >

                                    <Input
                                        placeholder="e.g. B.Ed, B.Sc"
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                DIVISIONS
                            ================================================== */}

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label="Assign Divisions"
                                    name="divisions"
                                >

                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        placeholder="Select divisions"
                                        optionFilterProp="label"
                                        options={
                                            divisions.map(
                                                (
                                                    division
                                                ) => ({
                                                    value:
                                                        division.id,

                                                    label:
                                                        division.name,
                                                })
                                            )
                                        }
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                CONTACT
                            ================================================== */}

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label="Contact"
                                    name="contact"
                                >

                                    <Input
                                        placeholder="Contact number"
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                SALARY
                            ================================================== */}

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label="Salary"
                                    name="salary"
                                >

                                    <InputNumber
                                        style={{
                                            width:
                                                "100%",
                                        }}
                                        placeholder="Salary"
                                        min={0}
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                DATE
                            ================================================== */}

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label="Date Added"
                                    name="date_add"
                                >

                                    <Input
                                        type="date"
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                DEPENDENT STUDENT
                            ================================================== */}

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Form.Item
                                    label="Dependent Student"
                                    name="dependent_student"
                                >

                                    <Input
                                        placeholder="Dependent student"
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                SUBJECTS
                            ================================================== */}

                            <Col
                                xs={24}
                            >

                                <Form.Item
                                    label="Assign Subjects"
                                    name="subjects"
                                >

                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        placeholder="Select subjects"
                                        optionFilterProp="label"
                                        options={
                                            subjects.map(
                                                (
                                                    subject
                                                ) => ({
                                                    value:
                                                        subject.id,

                                                    label:
                                                        subject.name,
                                                })
                                            )
                                        }
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                GRADE CLASSES
                            ================================================== */}

                            <Col
                                xs={24}
                            >

                                <Form.Item
                                    label="Assign Grade Classes"
                                    name="grade_class"
                                >

                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        placeholder="Select grade classes"
                                        optionFilterProp="label"
                                        options={
                                            gradeClasses.map(
                                                (
                                                    grade
                                                ) => ({
                                                    value:
                                                        grade.id,

                                                    label:
                                                        grade.name,
                                                })
                                            )
                                        }
                                    />

                                </Form.Item>

                            </Col>


                            {/* ==================================================
                                AUDIT INFORMATION
                            ================================================== */}

                            {editingId && (

                                <Col
                                    xs={24}
                                >

                                    <Card
                                        size="small"
                                        style={{
                                            background:
                                                "#f8fafc",
                                            border:
                                                "1px solid #e5e7eb",
                                        }}
                                    >

                                        <Space
                                            direction="vertical"
                                            size={2}
                                        >

                                            <Text
                                                strong
                                            >
                                                Employment Status Audit
                                            </Text>


                                            <Text
                                                type="secondary"
                                            >

                                                Status:
                                                {" "}
                                                {getEmploymentStatusDisplay(
                                                    teachers.find(
                                                        (teacher) =>
                                                            teacher.id ===
                                                            editingId
                                                    )
                                                )}

                                            </Text>


                                            {(() => {

                                                const currentTeacher =
                                                    teachers.find(
                                                        (teacher) =>
                                                            teacher.id ===
                                                            editingId
                                                    );


                                                if (
                                                    !currentTeacher
                                                ) {
                                                    return null;
                                                }


                                                return (

                                                    <>

                                                        {currentTeacher.status_changed_at && (

                                                            <Text
                                                                type="secondary"
                                                            >

                                                                Changed:
                                                                {" "}
                                                                {new Date(
                                                                    currentTeacher.status_changed_at
                                                                ).toLocaleString()}

                                                            </Text>

                                                        )}


                                                        {currentTeacher.status_changed_by && (

                                                            <Text
                                                                type="secondary"
                                                            >

                                                                Changed by:
                                                                {" "}
                                                                {
                                                                    typeof currentTeacher.status_changed_by ===
                                                                    "object"
                                                                        ? (
                                                                            currentTeacher.status_changed_by.username ||
                                                                            currentTeacher.status_changed_by.email ||
                                                                            "Administrator"
                                                                        )
                                                                        : currentTeacher.status_changed_by
                                                                }

                                                            </Text>

                                                        )}

                                                    </>

                                                );

                                            })()}

                                        </Space>

                                    </Card>

                                </Col>

                            )}

                        </Row>


                        {/* ==================================================
                            ACTIONS
                        ================================================== */}

                        <div
                            className="modal-actions"
                        >

                            <Button
                                onClick={() => {

                                    setOpen(
                                        false
                                    );

                                    resetForm();

                                }}
                            >

                                Cancel

                            </Button>


                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={
                                    loading
                                }
                            >

                                {editingId
                                    ? "Update Teacher"
                                    : "Save Teacher"}

                            </Button>

                        </div>

                    </Form>

                </Modal>

            </div>

        </>

    );

};


export default TeacherDataForm;